import { parseCanonicalIcd10Code } from "@smartmed/codes";

export type SexRestriction = "female" | "male";

export type PatientSex = "M" | "F";

export const isKnownSex = (sex: string | null | undefined): sex is PatientSex => sex === "M" || sex === "F";

export type SexMismatch = {
  required: SexRestriction;
  patientSex: PatientSex;
};

export function sexMismatch(
  restriction: SexRestriction | null | undefined,
  patientSex: string | null | undefined,
): SexMismatch | null {
  if (restriction === "female" && patientSex === "M") {
    return { required: "female", patientSex: "M" };
  }
  if (restriction === "male" && patientSex === "F") {
    return { required: "male", patientSex: "F" };
  }
  return null;
}

export const sexRestrictionsOf = (
  restrictionsByCode: ReadonlyMap<string, { genderRestriction: SexRestriction | null }>,
): ReadonlyMap<string, SexRestriction | null> =>
  new Map(Array.from(restrictionsByCode, ([code, { genderRestriction }]) => [code, genderRestriction]));

/** Looks each item up by its canonical ICD-10 code, falling back to the code as given. */
export function splitBySexFit<T extends { code: string }>(
  items: readonly T[],
  restrictionsByCode: ReadonlyMap<string, SexRestriction | null>,
  patientSex: string | null | undefined,
): { kept: T[]; removed: { item: T; mismatch: SexMismatch }[] } {
  const kept: T[] = [];
  const removed: { item: T; mismatch: SexMismatch }[] = [];
  for (const item of items) {
    const mismatch = sexMismatch(restrictionsByCode.get(parseCanonicalIcd10Code(item.code) ?? item.code), patientSex);
    if (mismatch) {
      removed.push({ item, mismatch });
    } else {
      kept.push(item);
    }
  }
  return { kept, removed };
}

export function sexMismatchText(mismatch: SexMismatch): string {
  return mismatch.required === "female" ? "Female-only code in male patient" : "Male-only code in female patient";
}
